import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Plus, Loader2, ShieldCheck, DownloadCloud, Clock, Mail } from 'lucide-react';
import { api, type AccessUser, ApiErr } from '../lib/api';
import { Spinner } from './ui';

/** Interrupteur compact « capacité » (Admin / Importateur). */
function Toggle({ on, busy, icon: Icon, label, onClick }: { on: boolean; busy: boolean; icon: typeof ShieldCheck; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      aria-pressed={on}
      title={label}
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition disabled:opacity-50 ${
        on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-muted hover:text-fg'
      }`}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

/**
 * Panneau « Accès & rôles » (roue dentée, admin seulement). On ajoute une personne
 * par e-mail — même avant sa 1ʳᵉ connexion : le rôle est hérité au login (lié par
 * e-mail). Deux capacités indépendantes par personne : Admin et Importateur.
 */
export function AccessSettings({ meId, onClose }: { meId?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<AccessUser[] | null>(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const [email, setEmail] = useState('');
  const [addAdmin, setAddAdmin] = useState(true);
  const [addImport, setAddImport] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void api.admin
      .users()
      .then(setUsers)
      .catch(() => setError(true));
  }, []);

  /** Insère ou remplace une ligne renvoyée par le serveur. */
  const upsertRow = (row: AccessUser) =>
    setUsers((prev) => {
      const rest = (prev ?? []).filter((u) => u.id !== row.id);
      return [...rest, row].sort((a, b) => Number(b.admin) - Number(a.admin) || a.email.localeCompare(b.email));
    });

  const invite = async (): Promise<void> => {
    const value = email.trim().toLowerCase();
    if (!value || adding) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      setNotice(t('access.invalidEmail'));
      return;
    }
    setAdding(true);
    setNotice(null);
    try {
      upsertRow(await api.admin.invite(value, { admin: addAdmin, canImport: addImport }));
      setEmail('');
    } catch {
      setNotice(t('error.generic'));
    }
    setAdding(false);
  };

  const toggle = async (u: AccessUser, key: 'admin' | 'canImport'): Promise<void> => {
    setBusyId(u.id);
    setNotice(null);
    try {
      upsertRow(await api.admin.setAccess(u.id, { [key]: !u[key] }));
    } catch (e) {
      // Garde-fou serveur : on ne retire pas le dernier administrateur.
      setNotice(e instanceof ApiErr && e.code === 'CONFLICT' ? t('access.lastAdmin') : t('error.generic'));
    }
    setBusyId(null);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle || !users) return users ?? [];
    return users.filter((u) => `${u.displayName} ${u.email}`.toLowerCase().includes(needle));
  }, [q, users]);

  const knownEmails = useMemo(() => (users ?? []).map((u) => u.email), [users]);

  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={t('access.title')} onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-surface shadow-xl ring-1 ring-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold">{t('access.title')}</h2>
            <p className="text-xs text-muted">{t('access.subtitle')}</p>
          </div>
          <button onClick={onClose} aria-label={t('access.close')} className="grid h-8 w-8 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-fg">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Ajouter par e-mail */}
        <div className="space-y-2 border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void invite()}
                list="gnosis-known-emails"
                type="email"
                placeholder={t('access.addPlaceholder')}
                className="w-full rounded-lg bg-surface-2 py-2 pl-9 pr-3 text-sm ring-1 ring-transparent outline-none placeholder:text-muted focus:ring-accent"
              />
              <datalist id="gnosis-known-emails">
                {knownEmails.map((e) => (
                  <option key={e} value={e} />
                ))}
              </datalist>
            </div>
            <button
              onClick={() => void invite()}
              disabled={adding || !email.trim()}
              className="flex shrink-0 items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-accent-fg disabled:opacity-40"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t('access.add')}
            </button>
          </div>
          <div className="flex items-center gap-2 pl-1">
            <span className="text-xs text-muted">{t('access.grant')}</span>
            <Toggle on={addAdmin} busy={false} icon={ShieldCheck} label={t('access.admin')} onClick={() => setAddAdmin((v) => !v)} />
            <Toggle on={addImport} busy={false} icon={DownloadCloud} label={t('access.importer')} onClick={() => setAddImport((v) => !v)} />
          </div>
          {notice && <p className="text-xs text-amber-600 dark:text-amber-400">{notice}</p>}
        </div>

        {/* Liste */}
        <div className="flex min-h-0 flex-1 flex-col px-4 py-3">
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('access.searchPlaceholder')}
              className="w-full rounded-lg bg-surface-2 py-1.5 pl-8 pr-3 text-sm outline-none ring-1 ring-transparent placeholder:text-muted focus:ring-accent"
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {error ? (
              <p className="px-1 py-2 text-sm text-muted">{t('error.generic')}</p>
            ) : !users ? (
              <Spinner label={t('explore.loading')} />
            ) : filtered.length === 0 ? (
              <p className="px-1 py-2 text-sm text-muted">{q ? t('access.noMatch', { q: q.trim() }) : t('access.empty')}</p>
            ) : (
              <ul className="space-y-1">
                {filtered.map((u) => (
                  <li key={u.id} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface-2">
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent/15 text-xs font-semibold uppercase text-accent">
                      {(u.displayName || u.email).slice(0, 2)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 truncate text-sm font-medium">
                        {u.pending ? u.email : u.displayName}
                        {u.id === meId && <span className="rounded bg-surface-2 px-1.5 text-xs text-muted">{t('access.you')}</span>}
                        {u.pending && (
                          <span className="flex items-center gap-1 rounded bg-amber-500/10 px-1.5 text-xs text-amber-600 dark:text-amber-400">
                            <Clock className="h-3 w-3" /> {t('access.pending')}
                          </span>
                        )}
                      </p>
                      {!u.pending && <p className="truncate text-xs text-muted">{u.email}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Toggle on={u.admin} busy={busyId === u.id} icon={ShieldCheck} label={t('access.admin')} onClick={() => void toggle(u, 'admin')} />
                      <Toggle on={u.canImport} busy={busyId === u.id} icon={DownloadCloud} label={t('access.importer')} onClick={() => void toggle(u, 'canImport')} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
